import { TestContext, object } from 'yup';

import KubernetesResourceReservationHelper from '@/kubernetes/helpers/resourceReservationHelper';
import { nanNumberSchema } from '@/react-tools/yup-schemas';

type NodeLimit = {
  CPU: number;
  Memory: number;
};

type NodesLimits = Record<string, NodeLimit>;

type ValidationData = {
  maxMemoryLimit: number;
  maxCpuLimit: number;
  isEnvironmentAdmin: boolean;
  nodeLimits: NodesLimits;
  isExistingCPUReservationUnchanged: boolean;
  isExistingMemoryReservationUnchanged: boolean;
  isExistingCPURequestUnchanged: boolean;
  isExistingMemoryRequestUnchanged: boolean;
};

function isUnlimitedValue(value: number | undefined) {
  return value === undefined || value === 0;
}

export function resourceReservationValidation(validationData?: ValidationData) {
  return object().shape({
    memoryRequest: nanNumberSchema('Memory request is required.')
      .min(0, 'Value must be greater than or equal to 0.')
      .test(
        'capacityAvailable',
        `The memory capacity for this namespace has been exhausted, so you cannot deploy the application.${
          validationData?.isEnvironmentAdmin
            ? ''
            : ' Contact your administrator to expand the memory capacity of the namespace.'
        }`,
        () => !!validationData && validationData.maxMemoryLimit > 0
      )
      .max(validationData?.maxMemoryLimit || 0, ({ value }) =>
        validationData?.isExistingMemoryRequestUnchanged
          ? `Value must be between 0 and ${validationData?.maxMemoryLimit}MB now - the previous value of ${value} exceeds this.`
          : `Value must be between 0 and ${validationData?.maxMemoryLimit}MB.`
      )
      .test(
        'hasSuitableNode',
        `These reservations would exceed the resources currently available in the cluster.`,
        (value: number | undefined, context: TestContext) => {
          if (!validationData || value === undefined) {
            return true;
          }
          const { cpuRequest } = context.parent;
          return hasSuitableNode(value, cpuRequest, validationData.nodeLimits);
        }
      )
      .required('Memory request is required.'),
    memoryLimit: nanNumberSchema()
      .min(0, 'Value must be greater than or equal to 0.')
      .test(
        'capacityAvailable',
        `The memory capacity for this namespace has been exhausted, so you cannot deploy the application.${
          validationData?.isEnvironmentAdmin
            ? ''
            : ' Contact your administrator to expand the memory capacity of the namespace.'
        }`,
        (value: number | undefined) => {
          // If no limit is set, skip capacity check
          if (isUnlimitedValue(value)) {
            return true;
          }
          return !!validationData && validationData.maxMemoryLimit > 0;
        }
      )
      .test(
        'withinMaxLimit',
        ({ value }) =>
          validationData?.isExistingMemoryReservationUnchanged
            ? `Value must be between 0 and ${validationData?.maxMemoryLimit}MB now - the previous value of ${value} exceeds this.`
            : `Value must be between 0 and ${validationData?.maxMemoryLimit}MB.`,
        (value: number | undefined) => {
          // If no limit is set, skip max limit check
          if (isUnlimitedValue(value)) {
            return true;
          }
          return value <= (validationData?.maxMemoryLimit || 0);
        }
      )
      .test(
        'hasSuitableNode',
        `These reservations would exceed the resources currently available in the cluster.`,
        (value: number | undefined, context: TestContext) => {
          if (!validationData || isUnlimitedValue(value)) {
            return true;
          }
          const { cpuLimit } = context.parent;
          return hasSuitableNode(
            value,
            cpuLimit || 0,
            validationData.nodeLimits
          );
        }
      )
      .test(
        'limitGreaterThanRequest',
        'Memory limit must be greater than or equal to memory request',
        (value: number | undefined, context: TestContext) => {
          // If no limit is set, skip this validation
          if (isUnlimitedValue(value)) {
            return true;
          }
          const { memoryRequest } = context.parent;
          return value >= memoryRequest;
        }
      ),
    cpuRequest: nanNumberSchema('CPU request is required.')
      .min(0)
      .test(
        'capacityAvailable',
        `The CPU capacity for this namespace has been exhausted, so you cannot deploy the application.${
          validationData?.isEnvironmentAdmin
            ? ''
            : ' Contact your administrator to expand the CPU capacity of the namespace.'
        }`,
        () => !!validationData && validationData.maxCpuLimit > 0
      )
      .max(validationData?.maxCpuLimit || 0, ({ value }) =>
        validationData?.isExistingCPURequestUnchanged
          ? `Value must be between 0 and ${validationData?.maxCpuLimit} now - the previous value of ${value} exceeds this.`
          : `Value must be between 0 and ${validationData?.maxCpuLimit}.`
      )
      .test(
        'hasSuitableNode',
        `These reservations would exceed the resources currently available in the cluster.`,
        (value: number | undefined, context: TestContext) => {
          if (!validationData || value === undefined) {
            return true;
          }
          const { memoryRequest } = context.parent;
          return hasSuitableNode(
            memoryRequest,
            value,
            validationData.nodeLimits
          );
        }
      )
      .required('CPU request is required.'),
    cpuLimit: nanNumberSchema()
      .min(0)
      .test(
        'capacityAvailable',
        `The CPU capacity for this namespace has been exhausted, so you cannot deploy the application.${
          validationData?.isEnvironmentAdmin
            ? ''
            : ' Contact your administrator to expand the CPU capacity of the namespace.'
        }`,
        (value: number | undefined) => {
          // If no limit is set, skip capacity check
          if (isUnlimitedValue(value)) {
            return true;
          }
          return !!validationData && validationData.maxCpuLimit > 0;
        }
      )
      .test(
        'withinMaxLimit',
        ({ value }) =>
          validationData?.isExistingCPUReservationUnchanged
            ? `Value must be between 0 and ${validationData?.maxCpuLimit} now - the previous value of ${value} exceeds this.`
            : `Value must be between 0 and ${validationData?.maxCpuLimit}.`,
        (value: number | undefined) => {
          // If no limit is set, skip max limit check
          if (isUnlimitedValue(value)) {
            return true;
          }
          return value <= (validationData?.maxCpuLimit || 0);
        }
      )
      .test(
        'hasSuitableNode',
        `These reservations would exceed the resources currently available in the cluster.`,
        (value: number | undefined, context: TestContext) => {
          if (!validationData || value === undefined || value === 0) {
            return true;
          }
          const { memoryLimit } = context.parent;
          return hasSuitableNode(
            memoryLimit || 0,
            value,
            validationData.nodeLimits
          );
        }
      )
      .test(
        'limitGreaterThanRequest',
        'CPU limit must be greater than or equal to CPU request',
        (value: number | undefined, context: TestContext) => {
          // If no limit is set, skip this validation
          if (isUnlimitedValue(value)) {
            return true;
          }
          const { cpuRequest } = context.parent;
          return value >= cpuRequest;
        }
      ),
  });
}

function hasSuitableNode(
  memoryValue: number,
  cpuValue: number,
  nodeLimits: NodesLimits
) {
  const nanParsedMemoryValue = Number.isNaN(memoryValue) ? 0 : memoryValue;
  const nanParsedCPUValue = Number.isNaN(cpuValue) ? 0 : cpuValue;
  // transform the nodelimits from bytes to MB
  const limits = Object.values(nodeLimits).map((nodeLimit) => ({
    ...nodeLimit,
    Memory: KubernetesResourceReservationHelper.megaBytesValue(
      nodeLimit.Memory
    ),
  }));
  // make sure there's a node available with enough memory and cpu
  return limits.some(
    (nodeLimit) =>
      nodeLimit.Memory >= nanParsedMemoryValue &&
      nodeLimit.CPU >= nanParsedCPUValue
  );
}
